import type { ReactNode } from 'react';
import { Button, Space, Table, Tag, Typography, message } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { registryRecordApi, type RegistryDocument, type RegistryEncounter } from '../../api/registryRecordApi';

const { Text } = Typography;

/** Etichetele valorilor stocate literal în registru (D-08, D-09). */
export const REGISTRY_LABELS: Record<string, string> = {
  // bază diagnostic
  HISTOLOGY: 'histopatologic',
  IMAGING: 'imagistic',
  CLINICAL: 'clinic',
  // categorie
  MALIGNANT: 'malign',
  IN_SITU: 'in situ',
  BORDERLINE: 'borderline',
  BENIGN: 'benign',
  METASTASIS: 'metastază',
  UNKNOWN: 'nedeterminat',
  // consimțământ
  TICKED: 'bifat',
  SIGNED_NO_TICKS: 'semnat fără bife',
  MISSING: 'lipsă',
  // episoade
  HOSPITALIZATION: 'internare',
  OBSERVATION: 'foaie de observație',
  AMBULATORY_LETTER: 'scrisoare medicală',
  ONCOLOGY_CONSULT: 'consult oncologic',
  // documente
  QUESTIONNAIRE: 'chestionar',
  DISCHARGE_LETTER: 'bilet de ieșire',
  TRANSFER_SHEET: 'foaie de transfer',
  OBSERVATION_SHEET: 'foaie de observație',
  MEDICAL_LETTER: 'scrisoare medicală',
  HP_BULLETIN: 'buletin histopatologic',
  HP_MISSING: 'lipsă HP',
  LAB_RESULTS: 'analize',
  IMAGING_REPORT: 'rezultat imagistic',
  FOTOFINDER_REPORT: 'raport FotoFinder',
  OTHER: 'alt document',
  // HP
  HP_ABSENT: 'buletin fără rezultat',
  RESULTED: 'cu rezultat',
  FREE: 'libere',
  CLOSE: 'apropiate',
  INVOLVED: 'interesate',
  NOT_ASSESSED: 'neevaluate',
  VALIDATED: 'validat',
  PENDING: 'în așteptare',
};

export const registryLabel = (value: string | null | undefined) =>
  value ? REGISTRY_LABELS[value] ?? value : '—';

export const registryDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString('ro-RO') : '—';

/** Deschide PDF-ul original al unui document-sursă într-un tab nou. */
export const openSourcePdf = (fileKey: string): void => {
  void registryRecordApi
    .openSourceFile(fileKey)
    .catch(() => message.error('PDF-ul nu a putut fi deschis'));
};

export interface DocGroup<T> {
  key: string;
  document: RegistryDocument | null;
  items: T[];
}

/**
 * Grupează rândurile după documentul-sursă (join pe documents[].id).
 * Grupurile urmează ordinea documentelor; rândurile fără document-sursă merg la final.
 */
export function groupBySourceDocument<T>(
  items: T[],
  documents: RegistryDocument[],
  getDocId: (item: T) => string | null | undefined,
): DocGroup<T>[] {
  const docIndex = new Map(documents.map((d, i) => [d.id, i]));
  const byId = new Map(documents.map((d) => [d.id, d]));
  const groups = new Map<string, DocGroup<T>>();
  for (const item of items) {
    const docId = getDocId(item);
    const document = docId ? byId.get(docId) ?? null : null;
    const key = document ? document.id : '__none__';
    let group = groups.get(key);
    if (!group) {
      group = { key, document, items: [] };
      groups.set(key, group);
    }
    group.items.push(item);
  }
  return [...groups.values()].sort((a, b) => {
    const ia = a.document ? docIndex.get(a.document.id)! : Number.MAX_SAFE_INTEGER;
    const ib = b.document ? docIndex.get(b.document.id)! : Number.MAX_SAFE_INTEGER;
    return ia - ib;
  });
}

/** Antetul unui grup de rânduri: documentul-sursă + link spre PDF-ul original. */
export function SourceDocHeader({ document }: { document: RegistryDocument | null }) {
  return document ? (
    <Space wrap size={8} style={{ margin: '8px 0 4px' }} data-testid={`doc-group-${document.id}`}>
      <Text strong>{document.fileName}</Text>
      <Tag>{registryLabel(document.kind)}</Tag>
      <Text type="secondary">{registryDate(document.documentDate)}</Text>
      <Button
        size="small"
        type="link"
        icon={<FilePdfOutlined />}
        style={{ padding: 0 }}
        onClick={() => openSourcePdf(document.fileKey)}
      >
        Vezi PDF
      </Button>
    </Space>
  ) : (
    <div style={{ margin: '8px 0 4px' }} data-testid="doc-group-none">
      <Text strong type="secondary">
        Fără document-sursă
      </Text>
    </div>
  );
}

/**
 * Randează grupurile cu antet de document; dacă nimic nu are document-sursă,
 * conținutul rămâne plat (fără antetul „Fără document-sursă”).
 */
export function renderDocGroups<T>(
  groups: DocGroup<T>[],
  renderTable: (items: T[]) => ReactNode,
): ReactNode {
  const flat = groups.length === 1 && groups[0].document === null;
  return groups.map((group) => (
    <div key={group.key}>
      {!flat && <SourceDocHeader document={group.document} />}
      {renderTable(group.items)}
    </div>
  ));
}

/** Linia de proveniență a unei secțiuni: documentul + data + „Vezi PDF”. */
export function SourceDocLine({ document }: { document: RegistryDocument | null | undefined }) {
  if (!document) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Sursă: {document.fileName}
        {document.documentDate ? ` · ${registryDate(document.documentDate)}` : ''}
      </Text>{' '}
      <Button
        size="small"
        type="link"
        icon={<FilePdfOutlined />}
        style={{ padding: 0 }}
        onClick={() => openSourcePdf(document.fileKey)}
      >
        Vezi PDF
      </Button>
    </div>
  );
}

/** Placeholder pentru o secțiune fără date în import. */
export function SectionEmpty({ text = 'Nedocumentat în import' }: { text?: string }) {
  return <Text type="secondary">{text}</Text>;
}

/** Tabelul de episoade clinice (tip, secție, perioadă, diagnostic, ICD-10, opțional proceduri). */
export function RegistryEncounterTable({
  encounters,
  showProcedures = false,
}: {
  encounters: RegistryEncounter[];
  showProcedures?: boolean;
}) {
  return (
    <Table<RegistryEncounter>
      size="small"
      rowKey="id"
      pagination={false}
      style={{ marginBottom: 16 }}
      dataSource={encounters}
      columns={[
        { title: 'Tip', key: 'type', render: (_, e) => registryLabel(e.type) },
        { title: 'Secția', dataIndex: 'unit', key: 'unit', render: (v: string | null) => v ?? '—' },
        {
          title: 'Perioadă',
          key: 'period',
          render: (_, e) => (e.endDate ? `${registryDate(e.startDate)} – ${registryDate(e.endDate)}` : registryDate(e.startDate)),
        },
        { title: 'Diagnostic', dataIndex: 'diagnosisText', key: 'dx', render: (v: string | null) => v ?? '—' },
        { title: 'ICD-10', key: 'icd', render: (_, e) => [e.principalIcd10, ...(e.secondaryIcd10 ?? [])].filter(Boolean).join(', ') || '—' },
        ...(showProcedures
          ? [
              {
                title: 'Proceduri',
                key: 'proc',
                render: (_: unknown, e: RegistryEncounter) =>
                  (e.procedures ?? []).map((p) => [p.code && `PO ${p.code}`, p.name].filter(Boolean).join(' ')).join('; ') || '—',
              },
            ]
          : []),
      ]}
    />
  );
}
